import type { CallMatch, CallSummary, TelephonyConnection, TelephonyExtension } from '../../api/telephony.dto.js';

/** 通話の表示ラベル（SFA 段階 5 §9.4）。一覧・詳細・着信ポップで揃える */
export const DIRECTION_LABEL: Record<CallSummary['direction'], string> = { inbound: '着信', outbound: '発信', internal: '内線' };
export const RESULT_LABEL: Record<NonNullable<CallSummary['result']>, string> = { answered: '応答', no_answer: '不在', busy: '話し中', cancelled: '取り消し', failed: '失敗', voicemail: '留守電', unknown: '不明' };
export const STATE_LABEL: Record<CallSummary['state'], string> = { ringing: '着信中', answered: '通話中', ended: '終了' };
export const CALLBACK_LABEL: Record<CallSummary['callbackState'], string> = { none: '', needed: '折り返し待ち', done: '折り返し済み', dismissed: '対応不要' };
export const RECORDING_LABEL: Record<CallSummary['recording']['state'], string> = { none: '録音なし', pending: '取り込み中', stored: '保存済み', deleted: '削除済み（保存期間の経過）', failed: '取り込みに失敗' };
export const LINK_SOURCE_LABEL: Record<NonNullable<CallSummary['linkSource']>, string> = { auto: '自動（番号の完全一致）', user: '手動', dial_intent: '発信元の画面' };
export const MATCH_KIND_LABEL: Record<CallMatch['kind'], string> = { contact: '連絡先', customer: '取引先', lead: '見込み客' };
export const MATCHED_BY_LABEL: Record<CallMatch['matchedBy'], string> = { phone_personal: '本人の番号', phone_company: '会社の代表番号', lead_phone: '見込み客の番号' };
export const EXTENSION_KIND_LABEL: Record<TelephonyExtension['matchKind'], string> = { extension: '内線番号', agent_id: 'エージェント ID', did: '着信番号（DID）' };
export const PROVIDER_LABEL: Record<TelephonyConnection['provider'], string> = { generic: '汎用（署名付き Webhook）', fake: 'テスト用（fake）',asterisk:'Asterisk PBX' };
export const AUTH_MODE_LABEL: Record<TelephonyConnection['authMode'], string> = { hmac: '署名（HMAC-SHA256）', token: 'トークン（ヘッダ）' };

export function durationText(sec: number | null): string {
  if (sec === null) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m} 分 ${s} 秒` : `${s} 秒`;
}

export function resultText(c: Pick<CallSummary, 'state' | 'result'>): string {
  return c.state === 'ended' ? (c.result ? RESULT_LABEL[c.result] : '—') : STATE_LABEL[c.state];
}

/** 紐づけ先の表示名。ID はあるが名前が無い = 見る人が読めない相手 */
export function linkedText(c: CallSummary): string {
  return c.contactName ?? c.leadName ?? c.customerName ?? (c.customerId || c.contactId || c.leadId || c.dealId ? '（読めない相手）' : '未紐づけ');
}

/** 紐づけ先・候補の画面の URL（見込み客の画面は /sales/leads） */
export function recordPath(kind: 'customer' | 'contact' | 'lead' | 'deal', id: string): string {
  switch (kind) {
    case 'customer':
      return `/crm/customers/${id}`;
    case 'contact':
      return `/crm/contacts/${id}`;
    case 'lead':
      return `/sales/leads/${id}`;
    case 'deal':
      return `/sales/deals/${id}`;
  }
}

/** 表示用の番号（E.164 の +81 を 0 始まりに戻すだけ。桁区切りは付けない） */
export function displayNumber(n: string | null | undefined): string {
  if (!n) return '非通知';
  return n.startsWith('+81') ? `0${n.slice(3)}` : n;
}

/** 冪等キー（operationId）。crypto.randomUUID が無い古い環境でも v4 形式を作る */
export function newOperationId(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const b = new Uint8Array(16);
  if (c) c.getRandomValues(b);
  else for (let i = 0; i < 16; i += 1) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export const CALL_READ = ['telephony.call.read.own', 'telephony.call.read.team', 'telephony.call.read.all'];
